import { api } from '@/lib/api';
import type { InsuranceFormData, LocalizedText } from '@fame/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export interface Insurance {
  id: string;
  projectId: string;
  internalName: string;
  description: LocalizedText;
  code: string;
  articleNumber: string;
  price: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  project?: {
    id: string;
    name: LocalizedText;
  };
}

export function useInsurances(projectId?: string, includeInactive = false, enabled = true) {
  const params = new URLSearchParams();
  if (projectId) params.append('projectId', projectId);
  if (includeInactive) params.append('includeInactive', 'true');
  const queryParams = params.toString() ? `?${params.toString()}` : '';

  return useQuery({
    queryKey: ['insurances', projectId, includeInactive],
    queryFn: () => api.get<Insurance[]>(`/insurances${queryParams}`),
    enabled,
  });
}

export function useInsurance(id: string) {
  return useQuery({
    queryKey: ['insurances', id],
    queryFn: () => api.get<Insurance>(`/insurances/${id}`),
    enabled: !!id,
  });
}

export function useCreateInsurance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InsuranceFormData) => api.post<Insurance>('/insurances', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurances'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Verzekering aangemaakt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij aanmaken verzekering: ${error.message}`);
    },
  });
}

export function useUpdateInsurance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InsuranceFormData> }) =>
      api.put<Insurance>(`/insurances/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['insurances'] });
      queryClient.invalidateQueries({ queryKey: ['insurances', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Verzekering bijgewerkt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij bijwerken verzekering: ${error.message}`);
    },
  });
}

export function useDeleteInsurance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/insurances/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurances'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Verzekering verwijderd');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij verwijderen verzekering: ${error.message}`);
    },
  });
}
